import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  deactivateSystemRole,
  getApiErrorMessage,
  listSystemRoles,
} from '../../api/client';
import type { SystemRole } from '../../api/types';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canEditRoles, canWriteRoles } from '../../utils/permissions';

export default function RolesPage() {
  const { user } = useAuth();
  const [roles, setRoles] = useState<SystemRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);

  const canCreate = user && canWriteRoles(user);
  const canModify = user && canEditRoles(user);

  function loadRoles() {
    setLoading(true);
    listSystemRoles()
      .then(setRoles)
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadRoles();
  }, []);

  async function handleDeactivate(id: string, name: string) {
    if (!window.confirm(`Deactivate role "${name}"? Users keep assignments but the role stops granting access.`)) {
      return;
    }
    setDeactivatingId(id);
    setError('');
    try {
      const updated = await deactivateSystemRole(id);
      setRoles((prev) => prev.map((r) => (r.id === id ? updated : r)));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setDeactivatingId(null);
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Role Management</h1>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Define system roles with per-module read, write, edit, and export permissions.
          </p>
        </div>
        {canCreate && (
          <Link to="/roles/new" className="btn btn-primary">
            Add role
          </Link>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        {loading ? (
          <div className="loading">Loading roles…</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Modules</th>
                  <th>Users</th>
                  <th>Status</th>
                  {canModify && <th />}
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {canModify ? (
                        <Link to={`/roles/${r.id}`}>{r.name}</Link>
                      ) : (
                        r.name
                      )}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{r.description ?? '—'}</td>
                    <td>{r.permissions.length}</td>
                    <td>{r._count.users}</td>
                    <td>
                      <Badge
                        label={r.isActive ? 'ACTIVE' : 'INACTIVE'}
                        className={r.isActive ? 'badge-success' : 'badge-neutral'}
                      />
                    </td>
                    {canModify && (
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {r.isActive && (
                          <button
                            type="button"
                            className="btn btn-danger-ghost"
                            disabled={deactivatingId === r.id}
                            onClick={() => void handleDeactivate(r.id, r.name)}
                          >
                            {deactivatingId === r.id ? '…' : 'Deactivate'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
